import { api } from '../api.js';
import { route, navigate, RouterLink } from '../router.js';
import { state } from '../state.js';
import { session as store } from '../session-store.js';
import { mmss, humanDate, humanDuration, scoreColor, plural, toRecording, Markdown, Empty }
  from '../ui.js';
import { Player } from '../player.js';
import { Talk } from '../talk.js';
import { ensureDictionaries } from '../bootstrap.js';

// Итоги одного собеса: запись, оценки, разбор, стенограмма.
// Клик по пункту разбора перематывает плеер на нужный момент — ради этого
// таймкоды и собираются.

export const Session = {
  components: { Markdown, Player, RouterLink, Empty, Talk },
  data() {
    // Только ручки этого экрана: плеер, таймеры, «пишу прямо сейчас». Данные
    // разбора лежат в session-store.js и переживают уход со страницы.
    return {
      refBusy: 0, refError: '', playing: 0, speaker: null,
      tailTimer: null, tailWaitedS: 0, tailShape: '', tailSame: 0,
      redoing: false,
      redoNote: '',
      seekTo: null,
    };
  },
  computed: {
    // Переходники к стору: разметка осталась прежней, источник один.
    loading: { get() { return store.loading; }, set(v) { store.loading = v; } },
    error: { get() { return store.error; }, set(v) { store.error = v; } },
    session: { get() { return store.info; }, set(v) { store.info = v; } },
    project: { get() { return store.project; }, set(v) { store.project = v; } },
    feedback: { get() { return store.feedback; }, set(v) { store.feedback = v; } },
    scores: { get() { return store.scores; }, set(v) { store.scores = v; } },
    points: { get() { return store.points; }, set(v) { store.points = v; } },
    plan: { get() { return store.plan; }, set(v) { store.plan = v; } },
    turns: { get() { return store.turns; }, set(v) { store.turns = v; } },
    questions: { get() { return store.questions; }, set(v) { store.questions = v; } },
    references: { get() { return store.references; }, set(v) { store.references = v; } },
    metrics: { get() { return store.metrics; }, set(v) { store.metrics = v; } },
    marks: { get() { return store.marks; }, set(v) { store.marks = v; } },
    hasAudio: { get() { return store.hasAudio; }, set(v) { store.hasAudio = v; } },
    tab: { get() { return store.tab; }, set(v) { store.tab = v; } },
    showTruth: { get() { return store.showTruth; }, set(v) { store.showTruth = v; } },
    tailPending: { get() { return store.tailPending; }, set(v) { store.tailPending = v; } },

    audioUrl() { return this.session ? `/api/media/sessions/${this.session.id}/audio` : ''; },
    // Прямая ссылка, а не запрос из скрипта: вход по куке, и файл сохраняется
    // средствами браузера — без сборки blob и утечки памяти на больших собесах.
    exportUrl() { return this.session ? `/api/interview/${this.session.id}/export.md` : ''; },
    exportName() { return this.session ? `собес-${this.session.seq || this.session.id}.md` : ''; },
    kindLabel() { return state.kinds.find((k) => k.id === this.session?.kind)?.name || 'Собес'; },
    canceled() { return this.session?.status === 'canceled'; },
    strong() { return this.points.filter((p) => p.kind === 'strong'); },
    weak() { return this.points.filter((p) => p.kind === 'weak'); },
    memorized() { return this.points.filter((p) => p.kind === 'memorized'); },
    hasReport() { return Boolean(this.feedback.trim()) || this.points.length > 0; },
    цена() { return store.цена; },
    за_неделю() { return store.заНеделю; },
    disputed() { return this.points.filter((p) => p.verdict === 'disputed').length; },
    // Итог формата и скрытый сценарий приходят вместе с сессией: пока собес
    // шёл, сервер их не отдавал — в них лежит то, что кандидат искал.
    outcome() { return this.session?.outcome || null; },
    scenario() { return this.session?.scenario || null; },
    outcomeTitle() {
      return { incident: 'Как прошёл разбор', review: 'Что нашёл в артефакте',
        pressure: 'Как держался под давлением' }[this.session?.kind] || 'Итог формата';
    },
  },
  async created() {
    await ensureDictionaries();
    await this.load();
  },
  beforeUnmount() { clearTimeout(this.tailTimer); this.hush(); },
  methods: {
    mmss, humanDate, humanDuration, scoreColor, plural,

    async load() {
      try {
        const data = await api.get(`/api/interview/${Number(route.params.id)}/report`);

        // Идущий собес разбирать нечего — возвращаем человека в разговор.
        // Раньше он попадал сюда и видел пустую карточку без единого намёка,
        // как вернуться обратно.
        if (data.session.status === 'active') {
          return navigate(`/interview/${data.session.id}`, true);
        }

        // Раскладывает стор: форма данных описана в одном месте, а не
        // повторяется тут и в каждом, кто их читает. Эталоны, написанные
        // раньше, приезжают вместе с отчётом.
        store.id = Number(route.params.id);
        store.take(data);
        this.watchTail();
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Разбор открывается сразу, а оценки, эталоны и запись доезжают следом:
     * это ещё три прохода агента и склейка звука. Раньше человек ждал их все
     * перед тем, как увидеть хоть строчку, — две-три минуты пустого экрана.
     * Теперь ждём здесь и молча дополняем страницу, когда они появятся.
     */
    watchTail() {
      clearTimeout(this.tailTimer);

      // Ждём ровно столько, сколько идёт сборка: сервер сам говорит, закончил
      // он или нет. Гадать по тому, «изменилось ли что-то на странице», было
      // ошибкой: части приезжают вразнобой, и запись — последней. Из-за этого
      // «записи нет» оставалось на экране у собеса, где запись давно склеена.
      if (!this.session?.wrap || this.tailWaitedS > 600) { this.tailPending = false; return; }
      this.tailPending = true;
      // Сначала часто, потом реже: у быстрого агента хвост доезжает за пару
      // секунд, у медленного — за минуту, и долбить сервер всё это время незачем.
      const wait = this.tailWaitedS < 10 ? 2000 : 6000;
      this.tailTimer = setTimeout(() => {
        this.tailWaitedS += wait / 1000;
        this.load();
      }, wait);
    },

    /**
     * Пересобрать оценки и план. Свободный разбор пишется всегда, а
     * структурированный проход мог не собраться — тогда график прогресса и
     * тренажёр слабых тем остаются пустыми, и починить это иначе нечем.
     */
    async redo() {
      this.redoing = true;
      const started = Date.now();

      // Ключ живёт до успеха. Пересборка идёт полторы минуты — два вызова
      // модели подряд, — и человек за это время успевает обновить страницу.
      // Без ключа повторное нажатие запускало бы всю работу заново, второй раз
      // за ту же подписку; с ключом оно подхватывает уже идущую.
      const store = `hh.redo.${this.session.id}`;
      const key = localStorage.getItem(store) || crypto.randomUUID();
      localStorage.setItem(store, key);

      // Секунды на экране: полторы минуты немой кнопки «Считаю…» читаются как
      // «ничего не происходит», и человек уходит перезагружать страницу.
      const tick = setInterval(() => {
        this.redoNote = `считаю… ${Math.round((Date.now() - started) / 1000)}с`;
      }, 1000);
      this.redoNote = 'считаю…';

      try {
        const r = await api.durable(`/api/interview/${this.session.id}/reanalyze`, { key });
        localStorage.removeItem(store);
        await this.load();
        this.redoNote = r.scores ? `готово: оценок ${r.scores}` : 'модель не дала оценок';
      } catch (e) {
        this.redoNote = 'не вышло: ' + e.message;
      } finally {
        clearInterval(tick);
        this.redoing = false;
      }
    },

    /**
     * Эталон считается по требованию: это вызов модели, и он нужен не для
     * каждого вопроса. Один раз посчитанный сохраняется навсегда.
     */
    async showReference(q) {
      if (this.references[q.id] || this.refBusy) return;
      this.refBusy = q.id;
      this.refError = '';
      try {
        const { body } = await api.post(`/api/interview/${this.session.id}/reference`,
          { question: q.id });
        this.references = { ...this.references, [q.id]: body };
      } catch (e) {
        this.refError = 'не вышло: ' + e.message;
      } finally {
        this.refBusy = 0;
      }
    },

    /**
     * Слушать эталон тем же голосом, каким говорил интервьюер. Повторное
     * нажатие останавливает: эталон бывает на две минуты, и уйти из него было
     * нечем — кнопка просто гасла на «читаю…» до самого конца.
     */
    say(q) {
      if (this.playing === q.id) return this.hush();
      this.hush();

      const text = this.references[q.id];
      if (!text) return;
      this.playing = q.id;
      const audio = new Audio(`/api/media/say?text=${encodeURIComponent(text)}`
        + `&voice=${encodeURIComponent(this.session.voice || '')}`);
      this.speaker = audio;
      audio.onended = () => { this.playing = 0; this.speaker = null; };
      audio.onerror = () => { this.playing = 0; this.speaker = null; this.refError = 'озвучить не вышло'; };
      audio.play().catch(() => { this.playing = 0; this.speaker = null; });
    },

    /** Замолчать: и по кнопке, и при уходе со страницы. */
    hush() {
      if (this.speaker) { this.speaker.pause(); this.speaker.src = ''; }
      this.speaker = null;
      this.playing = 0;
    },

    /** Значок строки итога: у форматов свои исходы, но читаются они одинаково. */
    markOf(v) {
      return { hit: '✓', held: '✓', fixed: '✓', extra: '+', miss: '✗', folded: '✗' }[v] || '·';
    },
    markWord(v) {
      return {
        hit: 'нашёл', miss: 'пропустил', extra: 'сверх списка',
        held: 'устоял', folded: 'сдался', fixed: 'признал и поправился',
      }[v] || '';
    },

    verdictWord(v) {
      return { strong: 'ответил уверенно', ok: 'ответил неполно', weak: 'не ответил' }[v] || '';
    },

    jump(at) {
      if (at == null) return;
      // Плеер слушает изменение seekTo; отдельный ref вызывал бы гонку при
      // ещё не подгруженных метаданных.
      this.seekTo = { at: toRecording(this.marks, at), nonce: Date.now() };
    },
  },
  template: `
    <div class="page" v-if="session">
      <p class="crumbs">
        <router-link to="/projects">Проекты</router-link> ·
        <router-link :to="'/project/' + project.id">{{ project.name }}</router-link>
      </p>

      <div class="row session-head">
        <div class="who">
          <h1>{{ kindLabel }} #{{ session.seq || session.id }}</h1>
          <p class="sub">
            {{ humanDate(session.started_at) }}<template v-if="session.duration_s">
            · {{ humanDuration(session.duration_s) }}</template>
            · {{ session.grade }}
          </p>
        </div>
        <a class="btn sm" :href="exportUrl" :download="exportName"
           title="разбор, оценки, план, вопросы и стенограмма одним файлом">
          ↓ Выгрузить в Markdown
        </a>
      </div>

      <!-- Обсуждение стоит вверху нарочно: вопросы появляются, пока читаешь
           разбор, а не после того, как долистал страницу до конца. -->
      <Talk :session="Number(session.id)" v-if="session" />

      <p class="faint cost" v-if="цена && цена.usd">
        Этот собес стоил {{ '$' + цена.usd }} · {{ цена.вызовов }}
        {{ plural(цена.вызовов, 'вызов', 'вызова', 'вызовов') }} агента<template
          v-if="за_неделю && за_неделю.usd"> · за неделю {{ '$' + за_неделю.usd }}</template>
      </p>

      <p class="err" v-if="error">{{ error }}</p>

      <div class="card" v-if="disputed" style="border-color:#5a2b2d;background:#241618">
        <p style="margin:0">
          Проверка нашла <strong>{{ disputed }}</strong>
          {{ plural(disputed, 'спорное место', 'спорных места', 'спорных мест') }} в разборе.
          Интервьюер оценивает из своей головы и может ошибаться — отмеченные
          пункты сверены с документацией, читай их с поправкой.
        </p>
      </div>

      <Player v-if="hasAudio" :src="audioUrl" :seek-to="seekTo" />
      <p class="caption" v-if="hasAudio">
        В записи только речь — пауз, пока думаешь и слушаешь вопрос, в ней нет,
        поэтому она короче собеса. Таймкоды в разборе — время собеса, плеер
        сам пересчитывает их в место записи.
      </p>
      <p class="faint" v-else style="margin-top:12px">
        Записи нет: собес прошёл без голоса или склейка не удалась.
      </p>

      <template v-if="outcome">
        <h2>{{ outcomeTitle }}</h2>
        <div class="card outcome">
          <div class="row" style="align-items:baseline;gap:12px">
            <b class="head" style="flex:1">{{ outcome.headline }}</b>
            <span class="tally" v-if="outcome.score">
              {{ outcome.score.got }}<i>из {{ outcome.score.of }}</i>
            </span>
          </div>
          <div class="orow" v-for="(r, i) in outcome.rows" :key="i" :class="r.verdict">
            <span class="mark">{{ markOf(r.verdict) }}</span>
            <div>
              <div class="label">{{ r.label }} <i>{{ markWord(r.verdict) }}</i></div>
              <div class="faint" v-if="r.note">{{ r.note }}</div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="scenario">
        <h2>Что было на самом деле</h2>
        <div class="card">
          <p class="note" style="margin-top:0">
            Пока собес шёл, этого не было ни на экране, ни в ответе сервера —
            иначе искать было бы нечего.
          </p>
          <button class="ghost sm" @click="showTruth = !showTruth" style="padding-left:0">
            {{ showTruth ? '− Скрыть' : '+ Показать' }}
          </button>
          <div v-show="showTruth" style="margin-top:12px">
            <Markdown :text="scenario.hidden"></Markdown>

            <template v-if="scenario.kind === 'incident'">
              <p><strong>Причина:</strong> {{ scenario.meta.root_cause }}</p>
              <p v-if="scenario.meta.chain && scenario.meta.chain.length">
                <strong>Цепочка:</strong> {{ scenario.meta.chain.join(' → ') }}
              </p>
              <p v-if="scenario.meta.red_herrings && scenario.meta.red_herrings.length">
                <strong>Ложные следы:</strong> {{ scenario.meta.red_herrings.join('; ') }}
              </p>
              <template v-if="scenario.meta.good_path && scenario.meta.good_path.length">
                <p><strong>Как шёл бы сильный кандидат:</strong></p>
                <ol class="path">
                  <li v-for="(g, i) in scenario.meta.good_path" :key="i">{{ g }}</li>
                </ol>
              </template>
            </template>

            <template v-if="scenario.kind === 'review' && scenario.meta.defects">
              <div class="qa" v-for="(d, i) in scenario.meta.defects" :key="i">
                <span class="tag" :class="{ bad: d.severity === 'high' }">{{ d.severity }}</span>
                <p class="ask">{{ d.where }} — {{ d.what }}</p>
                <p class="faint" style="margin:0">{{ d.why }}</p>
              </div>
            </template>

            <template v-if="scenario.kind === 'pressure' && scenario.meta.rounds">
              <div class="qa" v-for="(r, i) in scenario.meta.rounds" :key="i">
                <span class="tag" :class="r.attack === 'ложное' ? 'bad' : 'ok'">
                  возражение {{ r.attack }}
                </span>
                <p class="ask">{{ r.claim }}</p>
                <p class="faint" style="margin:0">Как на самом деле: {{ r.truth }}</p>
              </div>
            </template>
          </div>
        </div>
      </template>

      <!-- Пока оценок нет — говорим, что они собираются. Как появились,
           строка уходит: досбор эталонов и записи идёт молча. -->
      <p class="caption" v-if="tailPending">
        Разбор готов, его можно читать. Оценки по темам, эталонные ответы и
        запись собираются ещё пару минут — страница дополнится сама.
      </p>

      <div class="numbers" v-if="metrics && metrics.questions">
        <div><b>{{ metrics.questions }}</b>
          <span>{{ plural(metrics.questions, 'вопрос', 'вопроса', 'вопросов') }}</span></div>
        <div><b>{{ metrics.strong }}</b><span>уверенно</span></div>
        <div><b>{{ metrics.ok }}</b><span>неполно</span></div>
        <div><b>{{ metrics.weak }}</b><span>мимо</span></div>
        <div><b>{{ metrics.topics }}</b>
          <span>{{ plural(metrics.topics, 'тема', 'темы', 'тем') }}</span></div>
        <div><b>{{ metrics.myShare }}%</b><span>твоей речи</span></div>
        <div><b>{{ metrics.avgAnswer }}</b>
          <span>{{ plural(metrics.avgAnswer, 'слово', 'слова', 'слов') }} в ответе</span></div>
      </div>

      <nav class="tabs">
        <button :class="{ on: tab === 'report' }" @click="tab = 'report'">Разбор</button>
        <button :class="{ on: tab === 'scores' }" @click="tab = 'scores'">
          Оценки<i v-if="scores.length">{{ scores.length }}</i>
        </button>
        <button :class="{ on: tab === 'questions' }" @click="tab = 'questions'"
                v-if="questions.length">
          Вопросы<i>{{ questions.length }}</i>
        </button>
        <button :class="{ on: tab === 'transcript' }" @click="tab = 'transcript'">
          Стенограмма<i v-if="turns.length">{{ turns.length }}</i>
        </button>
      </nav>

      <!-- разбор -->
      <div v-if="tab === 'report'">
        <Empty v-if="!hasReport && canceled"
               title="Собес отменён"
               hint="Разбор не собирался — отмена для того и есть. Стенограмма на месте, её можно перечитать.">
        </Empty>
        <Empty v-else-if="!hasReport"
               title="Разбора нет"
               hint="Собес закрыли, не дойдя до разбора. Такое бывает, если выйти из него на первых репликах.">
        </Empty>

        <template v-else>
          <template v-if="points.length">
            <h2 v-if="weak.length">Где поплыл</h2>
            <div class="point weak" v-for="p in weak" :key="'w' + p.id">
              <div class="title">
                {{ p.title }}
                <span class="jump" v-if="p.at_s != null && hasAudio" @click="jump(p.at_s)">▶ {{ mmss(p.at_s) }}</span>
              </div>
              <div class="detail">{{ p.detail }}</div>
              <div class="cause" v-if="p.cause">Причина: {{ p.cause }}</div>
              <div class="correct" v-if="p.correct"><strong>Как правильно:</strong> {{ p.correct }}</div>
              <div class="verdict" :class="p.verdict" v-if="p.verdict && p.verdict !== 'confirmed'">
                <strong>{{ p.verdict === 'disputed' ? 'Проверка: спорно.' : 'Проверка: зависит от условий.' }}</strong>
                {{ p.verdict_note }}
                <template v-if="p.sources && p.sources.length">
                  <br><a v-for="(u, i) in p.sources" :key="i" :href="u" target="_blank"
                         style="margin-right:8px">источник {{ i + 1 }}</a>
                </template>
              </div>
              <div class="verdict confirmed" v-else-if="p.verdict === 'confirmed'">✓ проверено по документации</div>
            </div>

            <h2 v-if="memorized.length">Заучено, но не понято</h2>
            <div class="point memorized" v-for="p in memorized" :key="'m' + p.id">
              <div class="title">
                {{ p.title }}
                <span class="jump" v-if="p.at_s != null && hasAudio" @click="jump(p.at_s)">▶ {{ mmss(p.at_s) }}</span>
              </div>
              <div class="detail">{{ p.detail }}</div>
              <div class="cause" v-if="p.cause">Причина: {{ p.cause }}</div>
              <div class="correct" v-if="p.correct"><strong>Как правильно:</strong> {{ p.correct }}</div>
              <div class="verdict" :class="p.verdict" v-if="p.verdict && p.verdict !== 'confirmed'">
                <strong>{{ p.verdict === 'disputed' ? 'Проверка: спорно.' : 'Проверка: зависит от условий.' }}</strong>
                {{ p.verdict_note }}
                <template v-if="p.sources && p.sources.length">
                  <br><a v-for="(u, i) in p.sources" :key="i" :href="u" target="_blank"
                         style="margin-right:8px">источник {{ i + 1 }}</a>
                </template>
              </div>
              <div class="verdict confirmed" v-else-if="p.verdict === 'confirmed'">✓ проверено по документации</div>
            </div>

            <h2 v-if="strong.length">Сильные места</h2>
            <div class="point strong" v-for="p in strong" :key="'s' + p.id">
              <div class="title">
                {{ p.title }}
                <span class="jump" v-if="p.at_s != null && hasAudio" @click="jump(p.at_s)">▶ {{ mmss(p.at_s) }}</span>
              </div>
              <div class="detail">{{ p.detail }}</div>
            </div>

          </template>

          <template v-if="plan.length">
            <h2>Что выучить до следующего раза</h2>
            <div class="card">
              <div class="study" v-for="(it, i) in plan" :key="'p' + i">
                <span class="prio" :class="'p' + it.priority"
                      :title="it.priority === 1 ? 'без этого будет провал' : (it.priority === 2 ? 'важно' : 'желательно')">
                  {{ it.priority }}
                </span>
                <div>
                  <div><strong v-if="it.topic">{{ it.topic }}.</strong> {{ it.what }}</div>
                  <div class="faint" v-if="it.source">{{ it.source }}</div>
                </div>
              </div>
            </div>
          </template>

          <h2 v-if="points.length">Полный текст разбора</h2>
          <div class="card"><Markdown :text="feedback" /></div>
        </template>
      </div>

      <!-- оценки -->
      <div v-else-if="tab === 'scores'">
        <Empty v-if="!scores.length"
               title="Оценок нет"
               hint="Они появляются вместе с разбором. Если разбор есть, а оценок нет — структурированный проход не собрался. Без оценок пусты и график прогресса, и тренажёр слабых тем. Пересборка идёт полторы-две минуты: это два вызова модели подряд.">
          <button class="primary" style="margin-top:12px" @click="redo" :disabled="redoing">
            {{ redoing ? 'Считаю…' : 'Пересобрать разбор' }}
          </button>
          <p class="faint" v-if="redoNote">{{ redoNote }}</p>
        </Empty>
        <div class="card" v-else>
          <div class="scores">
            <div class="score-row" v-for="s in scores" :key="s.topic">
              <span :title="s.evidence">{{ s.topic }}</span>
              <span class="score-bar">
                <i :style="{ width: (s.score * 20) + '%', background: scoreColor(s.score) }"></i>
              </span>
              <span class="score-val">{{ s.score }}</span>
            </div>
          </div>
          <div class="row" style="margin-bottom:10px">
            <span class="spacer"></span>
            <button class="ghost sm" @click="redo" :disabled="redoing">
              {{ redoing ? 'Считаю…' : 'Пересобрать' }}
            </button>
            <span class="faint" v-if="redoNote">{{ redoNote }}</span>
          </div>
          <template v-for="s in scores" :key="'e' + s.topic">
            <div class="faint" style="margin-top:10px" v-if="s.evidence">
              <strong>{{ s.topic }}.</strong> {{ s.evidence }}
              <span class="jump" v-if="s.at_s != null && hasAudio" @click="jump(s.at_s)">▶ {{ mmss(s.at_s) }}</span>
            </div>
          </template>
        </div>
      </div>

      <!-- вопросы и эталонные ответы -->
      <div v-else-if="tab === 'questions'">
        <p class="note">
          Слева — как спросили и чем это кончилось. Эталон показывает, как на
          это ответил бы сильный кандидат вслух: не справка, а речь, за которой
          можно повторить. Он пишется в твоём стеке — по тем инструментам, что
          ты называл в разговоре.
          <!-- Эталоны на проваленное готовятся сами, вместе с разбором. Без
               этой строчки человек жмёт кнопку на одном вопросе, видит разом
               шесть готовых ответов и решает, что кнопка сломана. -->
          На то, что не далось, они готовятся сами — кнопка нужна для
          остальных.
        </p>
        <p class="err" v-if="refError">{{ refError }}</p>

        <div class="card qa" v-for="q in questions" :key="'q' + q.id">
          <div class="row">
            <span class="tag">{{ q.topic }}</span>
            <span class="faint" :class="q.verdict">{{ verdictWord(q.verdict) }}</span>
            <span class="spacer"></span>
            <span class="jump" v-if="q.at_s != null && hasAudio" @click="jump(q.at_s)">
              ▶ твой ответ {{ mmss(q.at_s) }}
            </span>
          </div>
          <p class="ask">{{ q.question }}</p>

          <div v-if="references[q.id]" class="reference">
            <p style="margin:0 0 8px">{{ references[q.id] }}</p>
            <button class="ghost sm" @click="say(q)">
              <span class="ico" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M4 6h2l3-2.5v9L6 10H4z"/><path d="M11 5.5a3.5 3.5 0 0 1 0 5"/></svg></span> {{ playing === q.id ? 'стоп' : 'прослушать' }}
            </button>
          </div>
          <button v-else class="sm" @click="showReference(q)" :disabled="refBusy">
            {{ refBusy === q.id ? 'Пишу…' : 'Как ответил бы сильный кандидат' }}
          </button>
        </div>
      </div>

      <!-- стенограмма -->
      <div v-else>
        <Empty v-if="!turns.length" title="Стенограммы нет" hint="Разговора не было."></Empty>
        <div class="card" v-else>
          <div v-for="(t, i) in turns" :key="i" style="margin-bottom:14px">
            <div class="faint">
              <!-- Когда ведущих двое, у реплики есть имя. Без него стенограмма
                   двойного собеса превращалась в один безымянный поток: кто из
                   них давил, а кто уводил в сторону, было не разобрать. -->
              {{ t.role === 'user' ? 'Я' : (t.speaker || 'Интервьюер') }}
              <span class="jump" v-if="hasAudio" @click="jump(t.at_s)">▶ {{ mmss(t.at_s) }}</span>
              <span class="jump" v-if="t.understood" @click="t.shown = !t.shown">
                {{ t.shown ? 'скрыть' : 'как понял интервьюер' }}
              </span>
            </div>
            <div style="white-space:pre-wrap">{{ t.body }}</div>
            <div class="heard" v-if="t.understood && t.shown">расслышал так: {{ t.understood }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="page" v-else-if="!loading">
      <Empty title="Собес не найден" hint="Возможно, он удалён вместе с проектом.">
        <router-link to="/projects"><button class="primary" style="margin-top:12px">К проектам</button></router-link>
      </Empty>
    </div>`,
};
